import React, { useState } from "react";
import { Upload, X, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return res.data.path;
}

// Single image uploader (returns a path/url string).
export const ImageUploader = ({ value, onChange, label, className = "", height = "h-40", testid }) => {
  const [loading, setLoading] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try { onChange(await uploadFile(file)); toast.success("Uploaded"); }
    catch { toast.error("Upload failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className={className}>
      {label && <label className="text-[11px] tracking-[0.12em] uppercase text-muted-foreground block mb-2">{label}</label>}
      <label data-testid={testid} className={`relative ${height} border border-dashed border-border hover:border-foreground transition-colors flex items-center justify-center cursor-pointer overflow-hidden bg-secondary/40 group`}>
        {value ? (
          <>
            <img src={fileUrl(value)} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs gap-2"><Upload size={14} /> Replace</div>
          </>
        ) : loading ? <Loader2 className="animate-spin text-muted-foreground" size={18} /> : (
          <span className="flex flex-col items-center gap-2 text-muted-foreground text-xs"><Upload size={18} /> Upload</span>
        )}
        <input type="file" accept="image/*" className="hidden" onChange={onFile} />
      </label>
      {value && <button type="button" onClick={() => onChange("")} className="text-xs text-destructive mt-2 flex items-center gap-1"><X size={12} /> Remove</button>}
    </div>
  );
};

// Multiple image gallery uploader (returns array of paths/urls).
export const GalleryUploader = ({ value = [], onChange, label, testid }) => {
  const [loading, setLoading] = useState(false);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setLoading(true);
    try {
      const paths = [];
      for (const f of files) paths.push(await uploadFile(f));
      onChange([...(value || []), ...paths]);
      toast.success(`${paths.length} image(s) added`);
    } catch { toast.error("Upload failed"); }
    finally { setLoading(false); }
  };
  return (
    <div>
      {label && <label className="text-[11px] tracking-[0.12em] uppercase text-muted-foreground block mb-2">{label}</label>}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {(value || []).map((img, i) => (
          <div key={i} className="relative h-24 group overflow-hidden border border-border">
            <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange(value.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 bg-black/70 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} /></button>
          </div>
        ))}
        <label data-testid={testid} className="h-24 border border-dashed border-border hover:border-foreground transition-colors flex items-center justify-center cursor-pointer text-muted-foreground">
          {loading ? <Loader2 className="animate-spin" size={16} /> : <Plus size={18} />}
          <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
        </label>
      </div>
    </div>
  );
};
